import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Award, Lightbulb, ArrowRight, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import PartnerSlider from '@/components/about/PartnerSlider';
import usePageTitle from '@/hooks/usePageTitle';

const AboutPage = () => {
  usePageTitle("About Us", "ElitePlus Solutions is a UAE-based IT consulting company delivering strategic technology advisory, digital transformation, cybersecurity, ERP, cloud, and AI-driven innovation services across the Middle East.");
  const differentiators = [
    {
      title: 'What We Do',
      items: [
        'Senior-led consulting',
        'Structured frameworks',
        'Clear deliverables & timelines',
        'Governance-led execution',
        'Flexible, outcome-focused engagement'
      ]
    },
    {
      title: 'What We Don\'t Do',
      items: [
        'One-size-fits-all implementations',
        'Junior-only delivery teams',
        'Open-ended engagements',
        'Hidden costs or scope creep',
        'Vendor-driven recommendations'
      ],
      isNegative: true
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Clarity First',
      description: 'We believe in providing clear roadmaps and expectations before any engagement begins.'
    },
    {
      icon: Users,
      title: 'Senior Expertise',
      description: 'Every engagement is led by consultants with 15+ years of enterprise experience.'
    },
    {
      icon: Award,
      title: 'Accountability',
      description: 'Clear deliverables, timelines, and measurable outcomes drive every project.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We bring fresh perspectives while respecting proven methodologies.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-6">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6">
              Our <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Philosophy</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Technology transformations fail not because of tools—but due to lack of clarity, governance, and execution discipline.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">
                Who We <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Are</span>
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  ElitePlus Solutions is a next-generation IT consulting and technology services company focused on helping organizations leverage digital technologies to achieve operational excellence, innovation, and sustainable growth.
                </p>
                <p>
                  We specialize in delivering strategic technology advisory, enterprise software solutions, cloud transformation, cybersecurity services, and AI-driven innovation that empower businesses to navigate complex digital landscapes.
                </p>
                <p>
                  Our mission is to enable organizations to transform their business models, optimize operations, and unlock new value through intelligent technology adoption. ElitePlus Solutions partners with organizations across industries to deliver scalable, secure, and future-ready digital ecosystems.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                alt="ElitePlus IT consulting team collaborating in UAE"
                loading="lazy"
                decoding="async"
                className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-500/10 rounded-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-teal-500/10 rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="relative py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1518235506717-e1ed3306a89b?w=800&h=600&fit=crop"
                alt="ElitePlus technology vision and digital transformation in UAE"
                loading="lazy"
                decoding="async"
                className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-teal-500/10 rounded-2xl" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-cyan-500/10 rounded-2xl" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-4">
                Our Vision
              </span>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">
                Leading Through <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Innovation</span>
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                To be a global leader in providing innovative, scalable, and sustainable technology solutions that empower organizations to achieve operational excellence and drive meaningful business outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-4">
                Our Mission
              </span>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">
                Driving <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Transformation</span>
              </h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    To deliver customized, cutting-edge software solutions that meet the evolving needs of modern businesses.
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    To help our clients navigate complex digital transformations with precision, insight, and strategic guidance.
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    To integrate emerging technologies into business operations to create competitive advantages and sustainable growth.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4623084/pexels-photo-4623084.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="ElitePlus mission for IT services and innovation in UAE"
                loading="lazy"
                decoding="async"
                className="rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-500/10 rounded-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-teal-500/10 rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <PartnerSlider />

      {/* How We're Different */}
      <section className="relative py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-4">
              How We're Different
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
              Our <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Approach</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {differentiators.map((section, idx) => (
              <div
                key={idx}
                className={`p-8 rounded-2xl border ${
                  section.isNegative
                    ? 'bg-white border-slate-200'
                    : 'bg-gradient-to-br from-cyan-500/5 to-teal-500/5 border-cyan-500/20'
                }`}
              >
                <h3 className="text-2xl font-bold text-slate-800 mb-6">{section.title}</h3>
                <div className="space-y-4">
                  {section.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        section.isNegative
                          ? 'bg-red-500/20'
                          : 'bg-gradient-to-br from-cyan-500 to-teal-500'
                      }`}>
                        {section.isNegative ? (
                          <X className="w-4 h-4 text-red-500" />
                        ) : (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
              What Drives <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Us</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div
                  key={idx}
                  className="group p-6 bg-white border border-slate-200 rounded-2xl hover:border-cyan-400/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-cyan-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-cyan-600 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">
            Let's Work <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Together</span>
          </h2>
          <p className="text-slate-500 text-lg mb-8">
            Ready to transform your IT landscape with clarity and confidence?
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
